import React, { useState, useEffect } from 'react'
import { LucideIcon, ChevronDown, Check, X } from 'lucide-react'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  children: React.ReactNode
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center font-medium rounded-button transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  
  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-primary-dark focus:ring-primary shadow-sm',
    secondary: 'bg-background-gray text-text-primary hover:bg-gray-200 focus:ring-gray-300',
    outline: 'border border-border-gray text-text-primary hover:bg-background-gray focus:ring-gray-300 bg-white',
    ghost: 'text-text-primary hover:bg-background-gray focus:ring-gray-300',
    danger: 'bg-error text-white hover:bg-red-600 focus:ring-error shadow-sm'
  }
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm min-h-8',
    md: 'px-4 py-2 text-sm min-h-10',
    lg: 'px-6 py-3 text-base min-h-12'
  }
  
  const isDisabled = disabled || loading
  
  const justifyClass = className.includes('justify-') ? '' : 'justify-center'
  
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} ${justifyClass} ${isDisabled ? 'pointer-events-none' : ''} ${className}`}
      disabled={isDisabled}
      {...props}
    >
      {loading && (
        <LoadingSpinner size="sm" className="mr-2" />
      )}
      {!loading && Icon && iconPosition === 'left' && (
        <Icon className="w-4 h-4 mr-2 flex-shrink-0" />
      )}
      <span className="truncate">{children}</span>
      {!loading && Icon && iconPosition === 'right' && (
        <Icon className="w-4 h-4 ml-2 flex-shrink-0" />
      )}
    </button>
  )
}

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
  color?: string
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  className = '',
  color = 'border-t-primary' 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  }
  
  return (
    <div 
      className={`animate-spin rounded-full border-2 border-gray-300 ${color} ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label="Loading"
    />
  )
}

interface CardProps {
  children: React.ReactNode
  className?: string
  padding?: 'none' | 'sm' | 'md' | 'lg'
  hover?: boolean
  elevated?: boolean
  bordered?: boolean
}

export const Card: React.FC<CardProps> = ({ 
  children, 
  className = '', 
  padding = 'md', 
  hover = false,
  elevated = false,
  bordered = true
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  }
  
  const shadowClass = elevated ? 'shadow-lg' : 'shadow-sm'
  const borderClass = bordered ? 'border border-border-gray' : ''
  
  return (
    <div className={`bg-white rounded-lg ${shadowClass} ${borderClass} ${paddingClasses[padding]} ${hover ? 'card-hover' : ''} ${className}`}>
      {children}
    </div>
  )
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  warning?: string
  success?: string
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  helpText?: string
}

export const Input: React.FC<InputProps> = ({ 
  label, 
  error, 
  warning,
  success,
  icon: Icon, 
  iconPosition = 'left',
  helpText,
  className = '', 
  ...props 
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`
  
  const getStatusColor = () => {
    if (error) return 'border-error focus:ring-error'
    if (warning) return 'border-warning focus:ring-warning'
    if (success) return 'border-success focus:ring-success'
    return 'border-border-gray focus:ring-primary focus:border-transparent'
  }

  const getIconColor = () => {
    if (error) return 'text-error'
    if (warning) return 'text-warning'
    if (success) return 'text-success'
    return isFocused ? 'text-primary' : 'text-text-muted'
  }

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-text-primary mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && iconPosition === 'left' && (
          <Icon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${getIconColor()}`} />
        )}
        <input
          id={inputId}
          className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 transition-colors ${
            Icon && iconPosition === 'left' ? 'pl-10' : ''
          } ${
            Icon && iconPosition === 'right' ? 'pr-10' : ''
          } ${getStatusColor()} ${className}`}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        />
        {Icon && iconPosition === 'right' && (
          <Icon className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${getIconColor()}`} />
        )}
      </div>
      {helpText && !error && !warning && !success && (
        <p className="mt-1 text-xs text-text-muted">{helpText}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
      {warning && (
        <p className="mt-1 text-sm text-warning">{warning}</p>
      )}
      {success && (
        <p className="mt-1 text-sm text-success">{success}</p>
      )}
    </div>
  )
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  warning?: string
  success?: string
  helpText?: string
  resize?: 'none' | 'vertical' | 'horizontal' | 'both'
}

export const Textarea: React.FC<TextareaProps> = ({ 
  label, 
  error, 
  warning,
  success,
  helpText,
  resize = 'vertical',
  className = '', 
  ...props 
}) => {
  const textareaId = props.id || `textarea-${Math.random().toString(36).substr(2, 9)}`
  
  const getStatusColor = () => {
    if (error) return 'border-error focus:ring-error'
    if (warning) return 'border-warning focus:ring-warning'  
    if (success) return 'border-success focus:ring-success'
    return 'border-border-gray focus:ring-primary focus:border-transparent'
  }

  const resizeClasses = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize'
  }

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={textareaId} className="block text-sm font-medium text-text-primary mb-1">
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 ${resizeClasses[resize]} transition-colors ${getStatusColor()} ${className}`}
        {...props}
      />
      {helpText && !error && !warning && !success && (
        <p className="mt-1 text-xs text-text-muted">{helpText}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
      {warning && (
        <p className="mt-1 text-sm text-warning">{warning}</p>
      )}
      {success && (
        <p className="mt-1 text-sm text-success">{success}</p>
      )}
    </div>
  )
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  warning?: string
  success?: string
  options: Array<{ value: string; label: string; disabled?: boolean }>
  placeholder?: string
  helpText?: string
}

export const Select: React.FC<SelectProps> = ({ 
  label, 
  error, 
  warning,
  success,
  options, 
  placeholder,
  helpText,
  className = '', 
  ...props 
}) => {
  const selectId = props.id || `select-${Math.random().toString(36).substr(2, 9)}`
  
  const getStatusColor = () => {
    if (error) return 'border-error focus:ring-error'
    if (warning) return 'border-warning focus:ring-warning'
    if (success) return 'border-success focus:ring-success'
    return 'border-border-gray focus:ring-primary focus:border-transparent'
  }

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-text-primary mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          className={`w-full px-3 py-2 border rounded-input focus:outline-none focus:ring-2 appearance-none pr-10 transition-colors ${getStatusColor()} ${className}`}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(option => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
      </div>
      {helpText && !error && !warning && !success && (
        <p className="mt-1 text-xs text-text-muted">{helpText}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
      {warning && (
        <p className="mt-1 text-sm text-warning">{warning}</p>
      )}
      {success && (
        <p className="mt-1 text-sm text-success">{success}</p>
      )}
    </div>
  )
}

interface BadgeProps {
  children: React.ReactNode
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  dot?: boolean
}

export const Badge: React.FC<BadgeProps> = ({ 
  children, 
  variant = 'default', 
  size = 'md', 
  className = '',
  dot = false 
}) => {
  const variantClasses = {
    default: 'bg-background-gray text-text-primary',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800'
  }
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-sm'
  }
  
  return (
    <span className={`inline-flex items-center font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}>
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          variant === 'success' ? 'bg-green-600' :
          variant === 'warning' ? 'bg-yellow-600' :
          variant === 'error' ? 'bg-red-600' :
          variant === 'info' ? 'bg-blue-600' : 'bg-gray-600'
        }`} />
      )}
      {children}
    </span>
  )
}

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  closeOnOverlayClick?: boolean
  showCloseButton?: boolean
}

export const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true
}) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl'
  }
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={closeOnOverlayClick ? onClose : undefined}
          aria-hidden="true"
        />
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className={`inline-block w-full ${sizeClasses[size]} p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg relative`}>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-text-muted hover:text-text-primary"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          
          {title && (
            <div className="mb-4 pr-8">
              <h3 className="text-lg font-medium text-text-primary">{title}</h3>
            </div>
          )}
          
          <div className="max-h-96 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ToastProps {
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  onClose: () => void
  action?: {
    label: string
    onClick: () => void
  }
}

export const Toast: React.FC<ToastProps> = ({ type, title, message, onClose, action }) => {
  const typeClasses = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  }

  const iconClasses = {
    success: 'text-green-400',
    error: 'text-red-400',
    warning: 'text-yellow-400',
    info: 'text-blue-400'
  }
  
  return (
    <div className={`p-4 border rounded-lg shadow-lg ${typeClasses[type]} animate-slide-up max-w-sm w-full`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {type === 'success' && <Check className={`w-5 h-5 ${iconClasses[type]}`} />}
          {type === 'error' && <X className={`w-5 h-5 ${iconClasses[type]}`} />}
          {type === 'warning' && <span className={`w-5 h-5 ${iconClasses[type]} font-bold text-center`}>!</span>}
          {type === 'info' && <span className={`w-5 h-5 ${iconClasses[type]} font-bold text-center`}>i</span>}
        </div>
        <div className="ml-3 flex-1">
          <h4 className="font-medium text-sm">{title}</h4>
          {message && <p className="mt-1 text-sm opacity-90">{message}</p>}
          {action && (
            <div className="mt-2">
              <button
                onClick={action.onClick}
                className="text-sm font-medium underline hover:no-underline"
              >
                {action.label}
              </button>
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="ml-4 flex-shrink-0 text-lg font-bold opacity-70 hover:opacity-100"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

interface TabsProps {
  tabs: Array<{ id: string; label: string; content: React.ReactNode; badge?: string | number }>
  activeTab: string
  onTabChange: (tabId: string) => void
  className?: string
  variant?: 'default' | 'pills'
}

export const Tabs: React.FC<TabsProps> = ({ 
  tabs, 
  activeTab, 
  onTabChange, 
  className = '',
  variant = 'default'
}) => {
  return (
    <div className={className}>
      <div className={`${variant === 'pills' ? 'bg-background-gray p-1 rounded-lg' : 'border-b border-border-gray'}`}>
        <nav className={`flex ${variant === 'pills' ? 'space-x-1' : 'space-x-8'}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`${
                variant === 'pills'
                  ? `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === tab.id
                        ? 'bg-white text-text-primary shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`
                  : `py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-text-primary hover:border-gray-300'
                    }`
              }`}
            >
              <span>{tab.label}</span>
              {tab.badge && (
                <Badge variant="info" size="sm" className="ml-2">
                  {tab.badge}
                </Badge>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div className="mt-4">
        {tabs.find(tab => tab.id === activeTab)?.content}
      </div>
    </div>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  showInfo?: boolean
  startIndex?: number
  endIndex?: number
  totalItems?: number
  showFirstLast?: boolean
  maxVisiblePages?: number
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showInfo = true,
  startIndex,
  endIndex,
  totalItems,
  showFirstLast = true,
  maxVisiblePages = 7
}) => {
  const getVisiblePages = () => {
    const delta = Math.floor(maxVisiblePages / 2)
    let start = Math.max(1, currentPage - delta)
    let end = Math.min(totalPages, start + maxVisiblePages - 1)
    
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1)
    }
    
    const pages = []
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    
    return pages
  }
  
  if (totalPages <= 1) return null
  
  const visiblePages = getVisiblePages()
  
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      {showInfo && startIndex && endIndex && totalItems && (
        <div className="text-sm text-text-secondary">
          Showing {startIndex} to {endIndex} of {totalItems} results
        </div>
      )}
      
      <div className="flex items-center space-x-1">
        {showFirstLast && currentPage > 1 && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
          </>
        )}
        
        {visiblePages[0] > 1 && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
            >
              1
            </Button>
            {visiblePages[0] > 2 && (
              <span className="px-2 text-text-muted">...</span>
            )}
          </>
        )}
        
        {visiblePages.map(page => (
          <Button
            key={page}
            variant={page === currentPage ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}
        
        {visiblePages[visiblePages.length - 1] < totalPages && (
          <>
            {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
              <span className="px-2 text-text-muted">...</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}
        
        {showFirstLast && currentPage < totalPages && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

interface SkeletonProps {
  className?: string
  width?: string | number
  height?: string | number
  rounded?: boolean
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  rounded = false
}) => {
  const style: React.CSSProperties = {}
  
  if (width) style.width = typeof width === 'number' ? `${width}px` : width
  if (height) style.height = typeof height === 'number' ? `${height}px` : height
  
  return (
    <div
      className={`animate-pulse bg-background-gray ${rounded ? 'rounded-full' : 'rounded'} ${className}`}
      style={style}
    />
  )
}

interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
    icon?: LucideIcon
  }
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action
}) => {
  return (
    <div className="text-center py-12">
      {Icon && <Icon className="w-16 h-16 text-text-muted mx-auto mb-4" />}
      <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
      {description && (
        <p className="text-text-secondary mb-6 max-w-md mx-auto">{description}</p>
      )}
      {action && (
        <Button onClick={action.onClick} icon={action.icon}>
          {action.label}
        </Button>
      )}
    </div>
  )
}